// ── 檔案上傳、印章、去背、回饋等 API ─────────────────────────────────────────

import { apiFetch, API_BASE_URL } from './client';

// ── Upload APIs ───────────────────────────────────────────────────────────────

export interface UploadImageResult {
  fileId: string;
  url: string;
  /** 列表縮圖（後端代理），缺失時 fallback 用 url */
  thumbnailUrl?: string;
  gcsPath: string;
}

/** 預先上傳單張圖片，回傳 fileId + URL + gcsPath */
export const uploadImageFull = async (file: File): Promise<UploadImageResult> => {
  const formData = new FormData();
  formData.append('image', file, file.name);
  const res = await apiFetch(`${API_BASE_URL}/files`, { method: 'POST', body: formData }, 30000);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || 'Image pre-upload failed');
  }
  return res.json();
};

export const uploadImage = async (source: string | File, filename = 'upload.png'): Promise<string> => {
  const formData = new FormData();

  if (typeof source === 'string') {
    const blob = await fetch(source).then(r => r.blob());
    const ext = blob.type.split('/')[1] || 'png';
    formData.append('image', blob, filename.replace(/\.[^.]+$/, `.${ext}`));
  } else {
    formData.append('image', source, source.name);
  }

  const res = await apiFetch(`${API_BASE_URL}/upload/image`, { method: 'POST', body: formData }, 30000);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err).error || 'Image upload failed');
  }
  const data = await res.json();
  return data.url as string;
};

// ── Stamp Preset APIs ─────────────────────────────────────────────────────────

export interface StampPresetItem {
  id: string;
  name: string;
  type: 'builtin' | 'custom';
  icon: string | null;
  imageUrl: string | null;
  /** 列表縮圖（後端代理），缺失時 fallback 用 imageUrl */
  imageThumbnailUrl?: string | null;
  builtinDataUrl: string | null;
  fileId: string | null;
  enabled: boolean;
  sortOrder: number;
}

/** 取得當前帳號所有印章預設 */
export const fetchStampPresets = async (): Promise<StampPresetItem[]> => {
  const res = await apiFetch(`${API_BASE_URL}/stamps`);
  if (!res.ok) throw new Error('Failed to fetch stamp presets');
  return res.json();
};

/** 新增自訂印章 */
export const createStampPreset = async (name: string, imageFile: File): Promise<StampPresetItem> => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('image', imageFile);

  const res = await apiFetch(`${API_BASE_URL}/stamps`, { method: 'POST', body: formData }, 30000);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || '新增印章失敗');
  }
  return res.json();
};

/** 更新印章（enabled / name） */
export const updateStampPreset = async (id: string, updates: { enabled?: boolean; name?: string }): Promise<void> => {
  const res = await apiFetch(`${API_BASE_URL}/stamps/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error('更新印章失敗');
};

/** 批次更新排序 */
export const reorderStampPresets = async (orders: { id: string; sortOrder: number }[]): Promise<void> => {
  const res = await apiFetch(`${API_BASE_URL}/stamps/reorder`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orders }),
  });
  if (!res.ok) throw new Error('排序更新失敗');
};

/** 刪除自訂印章 */
export const deleteStampPreset = async (id: string): Promise<void> => {
  const res = await apiFetch(`${API_BASE_URL}/stamps/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || '刪除印章失敗');
  }
};

/** 圖片去背（回傳 Blob） */
export const removeBackground = async (imageFile: File, signal?: AbortSignal): Promise<Blob> => {
  const formData = new FormData();
  formData.append('image', imageFile);

  const res = await fetch(`${API_BASE_URL}/stamps/remove-bg`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || '去背失敗');
  }

  return res.blob();
};

/** 去背 + 直接儲存為印章 */
export const removeBgAndSave = async (name: string, imageFile: File, signal?: AbortSignal): Promise<StampPresetItem> => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('image', imageFile);

  const res = await fetch(`${API_BASE_URL}/stamps/remove-bg-and-save`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || '去背儲存失敗');
  }

  return res.json();
};

// ── Feedback APIs ─────────────────────────────────────────────────────────────

export interface FeedbackPayload {
  message: string;
  rating?: 1 | 2 | 3;
  page: string;
  activityLog: unknown[];
  appSettings: Record<string, unknown>;
  userAgent: string;
}

export const submitFeedback = async (payload: FeedbackPayload): Promise<{ id: string }> => {
  const res = await apiFetch(`${API_BASE_URL}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as Record<string, string>).error || '回饋送出失敗');
  }
  return res.json();
};
